import moment from "moment";
import { DATE_FORMAT, ESIGN_IFRAME } from "utils/constants";
import {
  isBooleanFieldChecked,
  toBooleanFieldValue,
} from "utils/helpers/booleanField";
import { applyTrailingSpaceStrip } from "utils/helpers/validations";
import {
  applyDateInputBounds,
  resolveDateInputBounds,
  usesPlanDeltas,
} from "utils/validation-rules";
import { isCompleteFrenchZipCode, setupCitySelects } from "./citySelect";
import { setupSourceSelects, sourceOptionValueOf } from "./sourceSelect";
import { findFieldRoot, showFieldError } from "./fieldErrors";
import {
  ceilSliderValue,
  findSliderRoot,
  setupSliders,
  syncSlider,
} from "./slider";
import { applyHopBusy, markHopLoading } from "./hopBusy";
import {
  DATE_EDITING_ATTRIBUTE,
  DATE_FIELD_ATTRIBUTE,
  DATE_VALUE_EVENT,
  resolveEntityValue,
  resolveFieldPath,
  resolveStoredValue,
} from "./utils";

// ISO date / datetime values from the API (e.g. birthDate, issuanceDate).
// Datetimes are accepted but displayed as DATE_FORMAT (DD/MM/YYYY).
const ISO_DATE_PATTERN =
  /^\d{4}-\d{2}-\d{2}(?:[T\s]\d{2}:\d{2}(?::\d{2}(?:\.\d{1,3})?)?(?:Z|[+-]\d{2}:?\d{2})?)?$/;

// Native <input type="date"> emits 0001-MM-DD / 0019-MM-DD while the user
// is still typing the year. Persist only once the year has 4 digits.
const isPersistableDateValue = (value) => {
  if (value === "" || value == null) return true;
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return false;
  return Number(match[1]) >= 1000;
};

const ZIP_CODE_FIELD_PATTERN = /zipCode|codePostal|postalCode/i;

const isZipCodeControl = (el, doc) => {
  if (!el) return false;
  const accessor = el.getAttribute("data-field") || "";
  if (accessor && doc && doc.querySelector(`[data-city-for="${accessor}"]`)) {
    return true;
  }
  return ZIP_CODE_FIELD_PATTERN.test(accessor);
};

const entityFieldAccessor = (slot) => {
  const binding = slot && slot.binding;
  if (!binding || binding.kind !== "entity_field") return "";
  return binding.entity + "." + binding.field;
};

const bindingFromDataField = (accessor) => {
  if (!accessor || accessor.indexOf(".") < 0) return null;
  const dot = accessor.indexOf(".");
  return {
    kind: "entity_field",
    entity: accessor.slice(0, dot),
    field: accessor.slice(dot + 1),
  };
};

const formatTextValue = (val) => {
  if (val instanceof Date) {
    const parsed = moment(val);
    return parsed.isValid() ? parsed.format(DATE_FORMAT) : String(val);
  }
  if (typeof val !== "string" || !ISO_DATE_PATTERN.test(val.trim())) {
    return String(val);
  }
  const parsed = moment(val);
  return parsed.isValid() ? parsed.format(DATE_FORMAT) : String(val);
};

// Resolve option labels from the field registry when a data source maps to a
// field that declares options (works for any process, not a hardcoded map).
const resolveFieldDefForSource = (lookupFieldDef, entity, property) => {
  if (!lookupFieldDef || !property) return null;
  const direct = lookupFieldDef(entity, property);
  if (direct) return direct;
  // advanced.* values are often registered under the project group
  if (entity === "advanced") {
    return lookupFieldDef("project", property);
  }
  return null;
};

const formatOptionLabel = (lookupFieldDef, entity, property, val) => {
  if (val === undefined || val === null || val === "") return val;
  const fieldDef = resolveFieldDefForSource(lookupFieldDef, entity, property);
  const options = fieldDef && fieldDef.options;
  if (!Array.isArray(options) || !options.length) return val;
  const match = options.find((opt) => String(opt.value) === String(val));
  return match && match.label != null ? match.label : val;
};

const processText = ({ slot, el, ctx }) => {
  const { project, findValueGroup, lookupFieldDef } = ctx;
  if (!slot.binding) return;
  if (slot.binding.kind !== "data" || !slot.binding.source || !project) return;

  const source = slot.binding.source;
  // Generic current-date sources for display slots (e.g. "Émis le").
  if (source === "now" || source === "today") {
    el.textContent = formatTextValue(moment().toISOString());
    return;
  }

  const sourceParts = source.split(".");
  const sourceEntity = sourceParts[0];
  const sourceProperty = sourceParts.slice(1).join(".");

  const val = resolveEntityValue(project, sourceEntity, sourceProperty);
  const groupEl = findValueGroup ? findValueGroup(el) : null;
  if (val !== undefined && val !== null && val !== "") {
    const labeled = formatOptionLabel(
      lookupFieldDef,
      sourceEntity,
      sourceProperty,
      val,
    );
    el.textContent = formatTextValue(labeled);
    if (groupEl) groupEl.style.display = "";
    return;
  }
  const fallback = slot.binding.fallback;
  if (fallback !== undefined && fallback !== null && fallback !== "") {
    el.textContent = String(fallback);
    if (groupEl) groupEl.style.display = "";
    return;
  }
  el.textContent = "";
  if (groupEl && groupEl !== el) {
    groupEl.style.display = "none";
  }
};

export const resolveTarget = (slot, fieldDef) => {
  let target = fieldDef && fieldDef.target;
  if (
    !target &&
    slot.binding &&
    (slot.binding.entity === "advanced" || slot.binding.entity === "custom")
  ) {
    target = { entity: "project", property: "fields." + slot.binding.field };
  }
  if (
    !target &&
    slot &&
    slot.binding &&
    slot.binding.entity &&
    slot.binding.field
  ) {
    target = {
      entity: slot.binding.entity,
      property: slot.binding.field,
    };
  }
  return target;
};

export const readSlotValue = (slot, target, fieldDef, project) => {
  if (!project || !target) return undefined;
  let val;
  if (target.entity === "telecom" && target.filterValue) {
    // A pending edit is buffered as telecom.value.EMAIL and must win over the
    // last flushed row, the way resolveEntityValue does for other entities.
    const draft = (project.fields || {})[
      `${target.entity}.${target.property}.${target.filterValue}`
    ];
    if (draft !== undefined && draft !== null) return draft;
    const telecoms = resolveFieldPath(project, "contact.telecoms") || [];
    const telecom = telecoms.find((t) => t.system === target.filterValue);
    val = telecom ? telecom[target.property] : undefined;
  } else {
    val = resolveEntityValue(project, target.entity, target.property);
  }
  if (val !== undefined && val !== null) return val;
  let bufferKey = slot.binding.field;
  if (fieldDef && fieldDef.target) {
    bufferKey = fieldDef.target.filterValue
      ? `${fieldDef.target.entity}.${fieldDef.target.property}.${fieldDef.target.filterValue}`
      : `${fieldDef.target.entity}.${fieldDef.target.property}`;
  }
  return (project.fields || {})[bufferKey];
};

const processFile = ({ el, fieldDef, ctx }) => {
  const attachmentType =
    fieldDef && fieldDef.target && fieldDef.target.attachmentType;
  ctx.mountAttachmentField({
    el,
    attachmentType,
    projectId: ctx.project && ctx.project.id,
  });
};

const isEmptySelectValue = (val) =>
  val === undefined || val === null || val === "";

const EMPTY_STATE_INPUT_TYPES = new Set([
  "text",
  "email",
  "tel",
  "date",
  "number",
  "search",
]);

const syncSelectEmptyState = (el) => {
  if (!el || !el.setAttribute) return;
  if (el.tagName === "SELECT") {
    el.setAttribute("data-empty", el.value ? "false" : "true");
    return;
  }
  const inputType = el.type || "text";
  if (el.tagName === "INPUT" && EMPTY_STATE_INPUT_TYPES.has(inputType)) {
    el.setAttribute("data-empty", el.value ? "false" : "true");
  }
};

const readFieldDefault = (fieldDef) => {
  if (!fieldDef) return "";
  const value = fieldDef.defaultValue || fieldDef.default;
  if (value == null || value === "") return "";
  return String(value);
};

const applySelectPlaceholder = (el) => {
  const placeholder = Array.from(el.options).find(
    (option) => option.value === "",
  );
  if (!placeholder) return false;
  // Disabled empty options get dropped by some browsers after registry
  // options are appended. Keep the placeholder selectable so it stays empty.
  placeholder.disabled = false;
  placeholder.hidden = true;
  placeholder.selected = true;
  el.value = "";
  return true;
};

const processRemoteSelect = (el, val) => {
  if (!isEmptySelectValue(val)) {
    el.setAttribute("data-restore-value", String(val));
  } else {
    el.removeAttribute("data-restore-value");
  }
  syncSelectEmptyState(el);
};

const processSelect = ({ slot, el, val, fieldDef, ctx }) => {
  if (el.hasAttribute("data-city-for")) {
    processRemoteSelect(el, val);
    return;
  }
  if (el.hasAttribute("data-options-source")) {
    processRemoteSelect(el, sourceOptionValueOf(val));
    return;
  }
  const options = fieldDef && fieldDef.options;
  if (Array.isArray(options)) {
    for (const o of Array.from(el.options)) {
      if (!o.disabled && o.value !== "") o.remove();
    }
    for (const opt of options) {
      const optEl = ctx.doc.createElement("option");
      optEl.value = opt.value;
      optEl.textContent = opt.label;
      el.appendChild(optEl);
    }
  }
  if (!isEmptySelectValue(val)) {
    el.value = String(val);
    syncSelectEmptyState(el);
    return;
  }
  const fieldDefault = readFieldDefault(fieldDef);
  if (
    fieldDefault &&
    Array.from(el.options).some((option) => option.value === fieldDefault)
  ) {
    el.value = fieldDefault;
    if (slot && slot.binding) {
      ctx.persistFieldDefault(slot.binding, el.value);
    }
    syncSelectEmptyState(el);
    return;
  }
  // Keep the empty placeholder selected. Do not persist the first registry
  // option — Figma mockups may show a filled value that is not a default.
  if (applySelectPlaceholder(el)) {
    syncSelectEmptyState(el);
    return;
  }
  if (el.value && slot && slot.binding) {
    ctx.persistFieldDefault(slot.binding, el.value);
  }
  syncSelectEmptyState(el);
};

const processCheckbox = ({ el, val }) => {
  if (val === undefined || val === null) return;
  el.checked = isBooleanFieldChecked(val);
};

const toFilterValue = (field) =>
  String(field || "")
    .replace(/([a-z])([A-Z])/g, "$1_$2")
    .toUpperCase();

export const findFormField = (formDefinition, entity, field) => {
  const fields = (formDefinition && formDefinition.fields) || [];
  const accessor = entity + "." + field;
  const fieldsAccessor = "fields." + field;
  const filterAccessor = entity + ".value." + toFilterValue(field);
  return (
    fields.find(
      (item) =>
        item &&
        (item.accessor === accessor ||
          item.accessor === fieldsAccessor ||
          item.accessor === filterAccessor ||
          item.key === accessor ||
          item.key === field),
    ) || null
  );
};

export const mergeFieldDefinition = (registryField, formField) => {
  if (!registryField && !formField) return null;
  return {
    ...(registryField || {}),
    ...(formField || {}),
    inputConfig: {
      ...((registryField && registryField.inputConfig) || {}),
      ...((formField && formField.inputConfig) || {}),
    },
    rules:
      (formField && formField.rules) ||
      (registryField && registryField.rules) ||
      [],
  };
};

const dateFieldFocused = (el) => {
  const doc = el.ownerDocument;
  if (!doc) return false;
  if (doc.activeElement === el) return true;
  if (el.hasAttribute(DATE_EDITING_ATTRIBUTE)) return true;
  const anchor = el.nextElementSibling;
  const active = doc.activeElement;
  return Boolean(
    anchor &&
    active &&
    anchor.hasAttribute(DATE_FIELD_ATTRIBUTE) &&
    anchor.contains(active),
  );
};

const processDate = ({ el, val, fieldDef, ctx }) => {
  const editing = dateFieldFocused(el);
  if (!editing && val !== undefined && val !== null && val !== "") {
    el.value = moment(val).format("YYYY-MM-DD");
  } else if (!editing && val === "") {
    el.value = "";
  }
  if (editing) return;
  const view = el.ownerDocument && el.ownerDocument.defaultView;
  if (view) el.dispatchEvent(new view.Event(DATE_VALUE_EVENT));
  syncSelectEmptyState(el);
  const deltas = usesPlanDeltas(fieldDef && fieldDef.inputConfig)
    ? ctx.dateDeltas || {}
    : {};
  applyDateInputBounds(
    el,
    resolveDateInputBounds(fieldDef && fieldDef.inputConfig, {
      rules: (fieldDef && fieldDef.rules) || [],
      deltaStart: deltas.deltaStart,
      deltaEnd: deltas.deltaEnd,
    }),
  );
};

const processGenericInput = ({ slot, el, val, ctx }) => {
  const editing = el.ownerDocument && el.ownerDocument.activeElement === el;
  if (!editing && val !== undefined && val !== null) {
    el.value = String(val);
  } else if (el.type === "range" && el.value && slot && slot.binding) {
    ctx.persistFieldDefault(slot.binding, el.value);
  }
  syncSelectEmptyState(el);
  const slider = findSliderRoot(el);
  if (slider) syncSlider(slider);
};

const processSlider = ({ slot, el, val, ctx }) => {
  const root = findSliderRoot(el) || el;
  const input =
    el.tagName === "INPUT" && el.type === "range"
      ? el
      : el.querySelector("input[type='range']");
  if (!input) return;
  const hasValue = val !== undefined && val !== null && val !== "";
  const preferEstimate = root.hasAttribute("data-prefill-estimate");
  const estimate =
    ctx.project && ctx.project.fields && ctx.project.fields.capitalEstime;
  const hasEstimate =
    estimate !== undefined && estimate !== null && estimate !== "";
  let next = hasValue ? val : undefined;
  if (hasEstimate && (preferEstimate || !hasValue)) {
    const min = Number(root.getAttribute("data-min") || input.min || 0);
    const max = Number(root.getAttribute("data-max") || input.max || min);
    const step = Number(root.getAttribute("data-step") || input.step || 1);
    next = ceilSliderValue(estimate, min, max, step);
  }
  if (next !== undefined && next !== null && next !== "") {
    input.value = String(next);
    if ((!hasValue || preferEstimate) && slot && slot.binding) {
      ctx.persistFieldDefault(slot.binding, String(next));
    }
  } else if (input.value && slot && slot.binding) {
    ctx.persistFieldDefault(slot.binding, input.value);
  }
  syncSlider(root);
};

const companionFields = (el) => {
  const raw = el.getAttribute("data-fields");
  if (!raw) return [];
  return Object.entries(JSON.parse(raw)).map(([field, value]) => [
    { kind: "entity_field", entity: "advanced", field },
    value,
  ]);
};

const persistCompanionFields = (el, write) => {
  for (const [binding, value] of companionFields(el)) write(binding, value);
};

const processRadio = ({ slot, el, val, ctx }) => {
  if (!slot.radioGroupName) return;
  const dataValueButtons = el.querySelectorAll("[data-value]");
  if (dataValueButtons.length > 0) {
    if (val !== undefined && val !== null) {
      for (const btn of dataValueButtons) {
        btn.setAttribute(
          "data-selected",
          btn.getAttribute("data-value") === String(val) ? "true" : "false",
        );
      }
      return;
    }
    // Nothing saved yet: a card marked selected in the markup never emits a
    // click, so persist it now — same reason processSelect persists its default.
    const preselected = el.querySelector("[data-value][data-selected='true']");
    if (!preselected) return;
    ctx.persistFieldDefault(
      slot.binding,
      preselected.getAttribute("data-value"),
    );
    persistCompanionFields(preselected, ctx.persistFieldDefault);
    return;
  }
  const radios = ctx.doc.querySelectorAll(
    "input[type='radio'][name='" + slot.radioGroupName + "']",
  );
  if (!radios.length) return;

  for (let vi = 0; vi < radios.length; vi++) {
    if (!radios[vi].value || radios[vi].value === "on") {
      const labelText = (
        (radios[vi].closest("label") &&
          radios[vi].closest("label").textContent) ||
        ""
      )
        .replace(/\s+/g, " ")
        .trim()
        .toLowerCase();
      if (labelText.indexOf("oui") >= 0 || labelText.indexOf("yes") >= 0) {
        radios[vi].value = "yes";
      } else if (
        labelText.indexOf("non") >= 0 ||
        labelText.indexOf("no") >= 0
      ) {
        radios[vi].value = "no";
      } else {
        radios[vi].value = "option_" + vi;
      }
    }
  }
  if (val === undefined || val === null) return;
  for (const radio of radios) {
    const isChecked = radio.value === String(val);
    radio.checked = isChecked;
    const parentLabel = radio.closest("label");
    if (!parentLabel) continue;
    if (isChecked) {
      parentLabel.classList.add("border-primary");
      parentLabel.classList.remove("border-transparent");
    } else {
      parentLabel.classList.remove("border-primary");
      parentLabel.classList.add("border-transparent");
    }
  }
};

const inputProcessors = {
  file: processFile,
  select: processSelect,
  checkbox: processCheckbox,
  date: processDate,
  radioGroup: processRadio,
  slider: processSlider,
  range: processSlider,
};

const processInput = ({ slot, el, ctx }) => {
  if (!slot.binding || slot.binding.kind !== "entity_field") return;
  const fieldDef = ctx.lookupFieldDef(slot.binding.entity, slot.binding.field);
  if (slot.inputKind === "file") {
    return processFile({ el, fieldDef, ctx });
  }
  const target = resolveTarget(slot, fieldDef);
  const val = readSlotValue(slot, target, fieldDef, ctx.project);
  const processor = inputProcessors[slot.inputKind] || processGenericInput;
  return processor({ slot, el, val, fieldDef, ctx });
};

const processTextSlots = (ctx) => {
  for (const slot of ctx.slots) {
    if (slot.type !== "text") continue;
    const el = ctx.doc.querySelector("[data-kayros-slot='" + slot.id + "']");
    if (!el) continue;
    processText({ slot, el, ctx });
  }
};

const setMissingParams = (url, params) => {
  for (const [name, value] of Object.entries(params)) {
    if (!url.searchParams.has(name)) url.searchParams.set(name, value);
  }
};

export const toEsignFrameSrc = (value) => {
  let url;
  try {
    url = new URL(String(value));
  } catch (_error) {
    return null;
  }
  if (url.protocol !== ESIGN_IFRAME.PROTOCOL) return null;
  const provider = ESIGN_IFRAME.PROVIDERS.find(({ HOSTS }) =>
    HOSTS.includes(url.hostname),
  );
  if (!provider) return null;
  setMissingParams(url, provider.PARAMS);
  if (
    process.env.REACT_APP_YOUSIGN_SANDBOX === ESIGN_IFRAME.SANDBOX_ENV_VALUE
  ) {
    setMissingParams(url, provider.SANDBOX_PARAMS);
  }
  return url.toString();
};

const processImage = ({ slot, el, ctx }) => {
  if (!slot.binding) return;
  if (slot.binding.kind !== "data" || !slot.binding.source || !ctx.project)
    return;
  const sourceParts = slot.binding.source.split(".");
  const isFrame = el.tagName === ESIGN_IFRAME.TAG;
  const resolveValue = isFrame ? resolveStoredValue : resolveEntityValue;
  const val = resolveValue(
    ctx.project,
    sourceParts[0],
    sourceParts.slice(1).join("."),
  );
  const isEmpty = val === undefined || val === null || val === "";
  if (isFrame) {
    if (!Array.isArray(ctx.project.subscriptions)) return;
    const frameSrc = isEmpty ? null : toEsignFrameSrc(val);
    const currentSrc = el.getAttribute("src");
    if (!frameSrc || currentSrc === frameSrc) {
      ctx.cancelFrameLoad(el);
      if (!frameSrc && toEsignFrameSrc(currentSrc)) {
        el.setAttribute("src", ESIGN_IFRAME.BLANK_SRC);
      }
      return;
    }
    ctx.scheduleFrameLoad(el, frameSrc);
    return;
  }
  if (isEmpty) return;
  el.setAttribute("src", String(val));
};

const processImageSlots = (ctx) => {
  for (const slot of ctx.slots) {
    if (slot.type !== "image") continue;
    const el = ctx.doc.querySelector("[data-kayros-slot='" + slot.id + "']");
    if (!el) continue;
    processImage({ slot, el, ctx });
  }
};

const processInputSlots = (ctx) => {
  for (const slot of ctx.slots) {
    if (slot.type !== "input") continue;
    const el = ctx.doc.querySelector("[data-kayros-slot='" + slot.id + "']");
    if (!el) continue;
    processInput({ slot, el, ctx });
  }
};

const processDateSlots = (ctx) => {
  for (const slot of ctx.slots) {
    if (slot.type !== "input" || slot.inputKind !== "date") continue;
    if (!slot.binding || slot.binding.kind !== "entity_field") continue;
    const el = ctx.doc.querySelector("[data-kayros-slot='" + slot.id + "']");
    if (!el) continue;
    const fieldDef = ctx.lookupFieldDef(
      slot.binding.entity,
      slot.binding.field,
    );
    processDate({ slot, el, val: undefined, fieldDef, ctx });
  }
};

export const setupHydration = ({ doc, refs, handlers }) => {
  if (!doc) return undefined;

  const findValueGroup = (el) => {
    if (!el.hasAttribute("data-field")) return el;
    let cur = el.parentElement;
    let ancestor = el;
    let depth = 0;
    while (cur && cur !== doc.body && depth < 2) {
      const fields = cur.querySelectorAll("[data-field]");
      const others = [];
      for (const f of fields) {
        if (f !== el && !el.contains(f)) others.push(f);
      }
      if (others.length > 0) break;
      ancestor = cur;
      cur = cur.parentElement;
      depth++;
    }
    return ancestor;
  };

  const findSlotById = (id) => {
    if (!id) return null;
    const all = refs.slots.current || [];
    for (const s of all) {
      if (s && s.id === id) return s;
    }
    return null;
  };

  const findSlotByField = (accessor) => {
    if (!accessor) return null;
    const all = refs.slots.current || [];
    for (const candidate of all) {
      if (entityFieldAccessor(candidate) === accessor) return candidate;
    }
    return null;
  };

  const findSlotForControl = (el) => {
    if (!el || !el.getAttribute) return null;
    const accessor = el.getAttribute("data-field");
    const byField = findSlotByField(accessor);
    if (byField) return byField;
    const byId = findSlotById(el.getAttribute("data-kayros-slot"));
    const fromField = bindingFromDataField(accessor);
    if (fromField && entityFieldAccessor(byId) !== accessor) {
      return { ...(byId || {}), binding: fromField };
    }
    return byId;
  };

  const persistSliderValue = (value, input) => {
    if (!input || !refs.handleFieldChange.current) return;
    const root = findSliderRoot(input) || input;
    const slot = findSlotForControl(input) || findSlotForControl(root);
    if (slot && slot.binding) {
      refs.handleFieldChange.current(slot.binding, value);
    }
  };

  const resolveFormFieldForControl = (el) => {
    const formDefinition = refs.formDefinition && refs.formDefinition.current;
    const slot = findSlotById(el.getAttribute("data-kayros-slot"));
    if (slot && slot.binding && slot.binding.kind === "entity_field") {
      return findFormField(
        formDefinition,
        slot.binding.entity,
        slot.binding.field,
      );
    }
    const accessor = el.getAttribute("data-field");
    if (!accessor || accessor.indexOf(".") < 0) return null;
    const dot = accessor.indexOf(".");
    return findFormField(
      formDefinition,
      accessor.slice(0, dot),
      accessor.slice(dot + 1),
    );
  };

  const showErrorForControl = (el) => {
    if (!el) return;
    const formField = resolveFormFieldForControl(el);
    if (!formField) return;
    const messages =
      (refs.validationMessages && refs.validationMessages.current) || {};
    showFieldError(
      el,
      formField,
      messages,
      refs.project && refs.project.current,
    );
  };

  const persistBoundValue = (el, value) => {
    if (!el || !refs.handleFieldChange.current) return;
    const slot = findSlotForControl(el);
    if (slot && slot.binding) {
      refs.handleFieldChange.current(slot.binding, value);
    }
  };

  const teardownSliders = setupSliders(doc, {
    onValueChange: persistSliderValue,
  });

  const citySelects = setupCitySelects(doc, {
    fetchCities: handlers && handlers.fetchCities,
    onCityChange: persistBoundValue,
  });

  const sourceSelects = setupSourceSelects(doc, {
    loadOptions: handlers && handlers.loadOptions,
  });

  doc.addEventListener("input", (e) => {
    const target = e.target;
    if (!target || !target.getAttribute) return;
    syncSelectEmptyState(target);
    if (findSliderRoot(target)) return;

    const slot = findSlotForControl(target);
    if (!slot || !slot.binding || slot.binding.kind !== "entity_field") return;
    if (slot.inputKind === "checkbox") return;
    if (slot.inputKind === "file" || target.type === "file") return;
    if (
      (slot.inputKind === "date" || target.type === "date") &&
      !isPersistableDateValue(target.value)
    ) {
      return;
    }
    if (
      isZipCodeControl(target, doc) &&
      !isCompleteFrenchZipCode(target.value)
    ) {
      return;
    }
    refs.handleFieldChange.current(slot.binding, target.value);
    const root = target.closest && target.closest(".field");
    if (root && root.classList.contains("field--error")) {
      showErrorForControl(target);
    }
  });

  doc.addEventListener("change", (e) => {
    const target = e.target;
    if (!target || !target.getAttribute) return;

    const name = target.getAttribute && target.getAttribute("name");
    const radioSlot = name
      ? (refs.slots.current || []).find(
          (s) => s && s.inputKind === "radioGroup" && s.radioGroupName === name,
        )
      : null;
    if (radioSlot) {
      const group = doc.querySelectorAll(
        'input[type="radio"][name="' + name + '"]',
      );
      for (const r of group) {
        const label = r.closest("label");
        if (!label) continue;
        if (r.checked) {
          label.classList.add("border-primary");
          label.classList.remove("border-transparent");
        } else {
          label.classList.remove("border-primary");
          label.classList.add("border-transparent");
        }
      }
      if (radioSlot.binding && radioSlot.binding.kind === "entity_field") {
        refs.handleFieldChange.current(radioSlot.binding, target.value);
      }
      return;
    }
    const slot = findSlotForControl(target);
    if (!slot || !slot.binding || slot.binding.kind !== "entity_field") return;
    if (slot.inputKind === "checkbox") {
      refs.handleFieldChange.current(
        slot.binding,
        toBooleanFieldValue(target.checked),
      );
    } else if (slot.inputKind === "select") {
      syncSelectEmptyState(target);
      refs.handleFieldChange.current(slot.binding, target.value);
      showErrorForControl(target);
    }
  });

  doc.addEventListener("click", (e) => {
    const target = e.target;
    if (!target || !target.closest) return;

    const actionEl = target.closest("[data-action]");
    if (actionEl) {
      const action = actionEl.getAttribute("data-action");

      if (action === "addItem") {
        const collection =
          actionEl.getAttribute("data-collection") ||
          (actionEl.closest("[data-collection]") &&
            actionEl
              .closest("[data-collection]")
              .getAttribute("data-collection"));
        if (!collection) return;
        e.preventDefault();
        if (refs.onAddCollectionItem && refs.onAddCollectionItem.current) {
          refs.onAddCollectionItem.current(collection);
        }
        return;
      }
    }

    const dataValueBtn = target.closest("[data-value]");
    if (dataValueBtn) {
      // Prefer the radio group wrapper (`data-field`) over the button's own
      // action slot — choice cards often carry both data-value and next.
      const fieldEl = dataValueBtn.closest("[data-field]");
      const slotEl =
        (fieldEl && fieldEl.getAttribute("data-kayros-slot")
          ? fieldEl
          : null) || dataValueBtn.closest("[data-kayros-slot]");
      if (slotEl) {
        const slot = findSlotById(slotEl.getAttribute("data-kayros-slot"));
        if (
          slot &&
          slot.inputKind === "radioGroup" &&
          slot.binding &&
          slot.binding.kind === "entity_field"
        ) {
          e.preventDefault();
          const value = dataValueBtn.getAttribute("data-value");
          refs.handleFieldChange.current(slot.binding, value);
          persistCompanionFields(dataValueBtn, refs.handleFieldChange.current);
          const siblings = slotEl.querySelectorAll("[data-value]");
          for (const sib of siblings) {
            sib.setAttribute(
              "data-selected",
              sib.getAttribute("data-value") === value ? "true" : "false",
            );
          }
          const actionName = dataValueBtn.getAttribute("data-action");
          const buttonSlot = findSlotById(
            dataValueBtn.getAttribute("data-kayros-slot"),
          );
          if (actionName) {
            markHopLoading(dataValueBtn);
            applyHopBusy(doc, refs.slots && refs.slots.current, true);
            if (refs.onAction.current) {
              refs.onAction.current({
                kind: "process_trigger",
                name: actionName,
              });
            }
            return;
          }
          if (!(buttonSlot && buttonSlot.type === "action")) return;
        }
      }
    }

    const actionSlotEl = target.closest("[data-kayros-slot]");
    if (actionSlotEl) {
      const slot = findSlotById(actionSlotEl.getAttribute("data-kayros-slot"));
      if (slot && slot.type === "action" && slot.binding) {
        e.preventDefault();
        if (
          actionSlotEl.disabled ||
          actionSlotEl.getAttribute("data-krs-state") === "blocked" ||
          actionSlotEl.getAttribute("aria-disabled") === "true"
        ) {
          return;
        }
        // Only a run hop turns the renderer busy. set_field or download
        // actions stay inside the renderer, so marking them would leave a
        // spinner that nothing clears.
        if (slot.binding.kind === "process_trigger") {
          markHopLoading(actionSlotEl);
          applyHopBusy(doc, refs.slots && refs.slots.current, true);
        }
        if (refs.onAction.current) refs.onAction.current(slot.binding);
      }
    }
  });

  doc.addEventListener("focusout", (event) => {
    const target = event.target;
    if (!target || !target.closest) return;
    const control =
      target.closest("[data-kayros-slot]") || target.closest("[data-field]");
    if (!control) return;
    if (control.type === "checkbox") return;
    const root = findFieldRoot(control);
    const next = event.relatedTarget;
    if (root && next && root.contains(next)) return;
    if (next && next.closest && next.closest("[data-action='back']")) {
      return;
    }
    if (applyTrailingSpaceStrip(target)) {
      persistBoundValue(target, target.value);
    }
    showErrorForControl(control);
  });

  const lookupFieldDef = (entityKey, fieldKey) => {
    const reg = refs.registry.current || [];
    const findInEntity = (entity) => {
      const group = reg.find((item) => item.key === entity);
      return group
        ? group.fields.find((item) => item.key === fieldKey) || null
        : null;
    };
    const registryField =
      findInEntity(entityKey) ||
      (entityKey === "advanced" ? findInEntity("project") : null);
    const formField = findFormField(
      refs.formDefinition && refs.formDefinition.current,
      entityKey,
      fieldKey,
    );
    return mergeFieldDefinition(registryField, formField);
  };

  const persistFieldDefault = (binding, value) => {
    if (refs.handleFieldChange.current) {
      refs.handleFieldChange.current(binding, value);
    }
  };

  const pendingFrameLoads = new WeakMap();
  const frameTimers = new Set();

  const cancelFrameLoad = (el) => {
    const pending = pendingFrameLoads.get(el);
    if (!pending) return;
    clearTimeout(pending.timer);
    frameTimers.delete(pending.timer);
    pendingFrameLoads.delete(el);
  };

  const scheduleFrameLoad = (el, src) => {
    const pending = pendingFrameLoads.get(el);
    if (pending && pending.src === src) return;
    cancelFrameLoad(el);
    const timer = setTimeout(() => {
      frameTimers.delete(timer);
      pendingFrameLoads.delete(el);
      if (!el.isConnected || el.getAttribute("src") === src) return;
      el.setAttribute("src", src);
    }, ESIGN_IFRAME.LOAD_DELAY_MS);
    frameTimers.add(timer);
    pendingFrameLoads.set(el, { src, timer });
  };

  const buildCtx = () => ({
    doc,
    slots: refs.slots.current,
    project: refs.project.current,
    findValueGroup,
    lookupFieldDef,
    persistFieldDefault,
    scheduleFrameLoad,
    cancelFrameLoad,
    mountAttachmentField: handlers.mountAttachmentField,
    dateDeltas: (refs.dateDeltas && refs.dateDeltas.current) || {},
  });

  refs.conditionalEvaluator.current = (changedField, newValue) => {
    const slots = refs.slots.current;
    for (const slot of slots) {
      if (slot.binding && slot.binding.kind === "conditional") {
        if (slot.binding.field === changedField) {
          const condEl = doc.querySelector(
            "[data-kayros-slot='" + slot.id + "']",
          );
          if (condEl) {
            const expectedVal = slot.binding.value || "yes";
            condEl.style.display =
              String(newValue) === expectedVal ? "" : "none";
          }
        }
      }
    }
  };

  const evaluateConditionals = () => {
    const slots = refs.slots.current;
    const project = refs.project.current;
    if (!project) return;
    for (const slot of slots) {
      if (!slot.binding || slot.binding.kind !== "conditional") continue;
      const el = doc.querySelector("[data-kayros-slot='" + slot.id + "']");
      if (!el) continue;
      const condField = slot.binding.field;
      const condParts = condField.split(".");
      const condEntity = condParts[0];
      const condProperty = condParts.slice(1).join(".");
      let condTarget = { entity: condEntity, property: condProperty };
      if (condEntity === "advanced") {
        condTarget = {
          entity: "project",
          property: "fields." + condProperty,
        };
      }
      const condVal = resolveEntityValue(
        project,
        condTarget.entity,
        condTarget.property,
      );
      const expectedVal = slot.binding.value || "yes";
      el.style.display = String(condVal) === expectedVal ? "" : "none";
    }
  };

  const persistCompleteZipControls = () => {
    if (!refs.handleFieldChange.current) return;
    const fields = doc.querySelectorAll("[data-field]");
    for (const el of fields) {
      if (!isZipCodeControl(el, doc)) continue;
      if (!isCompleteFrenchZipCode(el.value)) continue;
      const accessor = el.getAttribute("data-field");
      const stamped = findSlotById(el.getAttribute("data-kayros-slot"));
      if (entityFieldAccessor(stamped) === accessor) continue;
      const slot = findSlotForControl(el);
      if (slot && slot.binding && slot.binding.kind === "entity_field") {
        refs.handleFieldChange.current(slot.binding, el.value);
      }
    }
  };

  refs.hydrate.current = {
    slotProcessors: [
      { fn: () => processTextSlots(buildCtx()), refire: true },
      { fn: () => processImageSlots(buildCtx()), refire: true },
      { fn: () => processInputSlots(buildCtx()), refire: true },
      { fn: () => processDateSlots(buildCtx()), refire: true },
      { fn: persistCompleteZipControls, refire: true },
      { fn: () => citySelects.refresh(), refire: true },
      { fn: () => sourceSelects.refresh(), refire: true },
    ].filter((x) => x),
    evaluateConditionals,
    lookupFieldDef,
  };

  return () => {
    if (teardownSliders) teardownSliders();
    if (citySelects && citySelects.teardown) citySelects.teardown();
    for (const timer of frameTimers) clearTimeout(timer);
    frameTimers.clear();
  };
};
